import classnames from 'classnames';
import { getColumnIndex, getRowIndex } from 'components/batchForm/BatchForm.helpers';
import { BatchFormCell } from 'components/batchForm/BatchFormCell';
import { DataApi, DataContext } from 'components/common-form/contexts/DataContext';
import { FieldMetadataContext } from 'components/common-form/contexts/FieldMetadataContext';
import { QuotedContext } from 'components/common-form/contexts/QuotedContext';
import { WithInvalidFieldsContext } from 'components/common-form/contexts/WithInvalidFieldsContext';
import { WithPatchedFieldsContext } from 'components/common-form/contexts/WithPatchedFieldsContext';
import type { QuoteKeys, QuoteKeysPrefix } from 'locales/quoteKeys';
import { isEmpty } from 'lodash-es';
import type { FormRecord } from 'models/formError';
import { FieldMetadata } from 'models/forms/fields/FieldMetadata';
import { FC, useMemo } from 'react';
import styled from 'styled-components';

import { FormOrientationContext } from '../common-form/contexts/FormOrientationContext';
import { ProductViewContext } from '../common-form/contexts/ProductViewContext';
import { connectedBatchForm } from './BatchForm.connect';
import type { BatchFormProps } from './BatchForm.models';

export const StyledFormCellWrapper = styled.div`
  font-weight: 500;
  font-feature-settings: 'cv01', 'cv10', 'tnum';
`;

export const BatchFormComponent: FC<BatchFormProps> = ({
  tabId,
  formId,
  productView,
  productDescription,
  formIndex,
  fieldsMetadata,
  inputData,
  pendingEnrichmentData,
  enrichedData,
  resultData,
  isQuoted,
  isDisabled,
  setFieldValue,
  toggleSelectForm,
  validationErrors,
  batchWorkspaceVersion,
  formStatus,
}) => {
  const data = useMemo(
    () => ({
      ...enrichedData,
      ...pendingEnrichmentData,
      ...inputData,
      ...resultData,
    }),
    [enrichedData, pendingEnrichmentData, inputData, resultData],
  );
  const dataApi: DataApi = useMemo(() => ({ setFieldValue }), [setFieldValue]);

  return isEmpty(data) ? (
    <>
      {productDescription.map(blockNode =>
        blockNode.children?.map((fieldNode, fieldIndex, fieldNodeList) => (
          <div
            key={fieldNode.id}
            className={classnames(
              fieldIndex === fieldNodeList.length - 1
                ? 'batch-grid-cell with-border-bottom-thick'
                : 'batch-grid-cell with-border-bottom-thin',
              'form-loading',
            )}
            data-e2e="batch-grid-cell"
          />
        )),
      )}
    </>
  ) : (
    <ProductViewContext.Provider value={productView}>
      <QuotedContext.Provider value={isQuoted}>
        <FieldMetadataContext.Provider value={fieldsMetadata}>
          <DataContext.Provider value={[data, dataApi]}>
            <WithPatchedFieldsContext formId={formId}>
              <WithInvalidFieldsContext<FormRecord, QuoteKeys, QuoteKeysPrefix> validationErrors={validationErrors}>
                <FormOrientationContext.Provider value="grid">
                  {productDescription.map((blockNode, blockIndex) =>
                    blockNode.children?.map((fieldNode, fieldIndex, fieldNodeList) => {
                      return (
                        <BatchFormCell
                          key={fieldNode.id}
                          tabId={tabId}
                          formId={formId}
                          batchWorkspaceVersion={batchWorkspaceVersion}
                          fieldNode={fieldNode}
                          fieldIndex={fieldIndex}
                          rowIndex={getRowIndex(blockIndex, fieldIndex, productDescription)}
                          columnIndex={getColumnIndex(formIndex)}
                          isDisabled={isDisabled}
                          fieldNodeList={fieldNodeList}
                          metadata={fieldsMetadata[fieldNode.id] as FieldMetadata | undefined}
                          selectFormCallback={toggleSelectForm}
                          formStatus={formStatus}
                        />
                      );
                    }),
                  )}
                </FormOrientationContext.Provider>
              </WithInvalidFieldsContext>
            </WithPatchedFieldsContext>
          </DataContext.Provider>
        </FieldMetadataContext.Provider>
      </QuotedContext.Provider>
    </ProductViewContext.Provider>
  );
};

export const BatchForm = connectedBatchForm(BatchFormComponent);
